import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { SiteTopBar } from "../components/site/SiteTopBar";
import { Footer } from "../components/site/Footer";
import { ImagePicker } from "../components/site/ImagePicker";
import { registerMerchant, loginMerchant, updateStore } from "../lib/merchantApi";

const idPlaceholder = { cnpj: "00.000.000/0000-00", cpf: "000.000.000-00", email: "seu@email.com" };

export default function Cadastro() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [idType, setIdType] = useState("cnpj");
  const [form, setForm] = useState({ name: "", identifier: "", email: "", phone: "", password: "" });
  const [logoUrl, setLogoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitStep1 = async (e) => {
    e.preventDefault();
    if (!form.name || !form.identifier || !form.password) {
      toast.error("Preencha nome da loja, identificador e senha");
      return;
    }
    setLoading(true);
    try {
      const email = idType === "email" ? form.identifier : form.email;
      await registerMerchant({
        name: form.name,
        store_name: form.name,
        email: (email || "").toLowerCase(),
        cnpj: form.identifier.replace(/\D/g, "") || form.identifier,
        phone: form.phone,
        password: form.password,
        accept_pix: true,
        accept_card: true,
        accept_cash_on_delivery: true,
      });
      await loginMerchant(form.identifier, form.password);
      toast.success("Conta criada! Agora envie sua identidade visual.");
      setStep(2);
    } catch (err) {
      toast.error(err.message || "Não foi possível concluir o cadastro");
    } finally {
      setLoading(false);
    }
  };

  const finish = async () => {
    setLoading(true);
    try {
      const patch = {};
      if (logoUrl) patch.image = logoUrl;
      if (coverUrl) patch.cover = coverUrl;
      if (Object.keys(patch).length) await updateStore(patch);
      setStep(3);
    } catch (err) {
      toast.error(err.message || "Falha ao salvar as imagens");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteTopBar />
      <main className="mx-auto max-w-2xl px-5 pb-24 pt-32 md:pt-40">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-maraca-teal">Seja um parceiro</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-maraca-navy md:text-5xl">
          Cadastre sua loja
        </h1>
        <p className="mt-4 text-maraca-navy/70">Em poucos passos sua loja entra no Delivery Maracás.</p>

        <div className="mt-8 flex items-center gap-2" data-testid="cadastro-steps">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-1.5 flex-1 rounded-full ${step >= n ? "bg-maraca-teal" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={submitStep1} className="mt-8 space-y-5" data-testid="cadastro-form">
            <div>
              <label className="mb-1.5 block text-sm font-bold text-maraca-navy">Nome da loja</label>
              <input data-testid="input-name" value={form.name} onChange={set("name")} className="w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-maraca-teal" placeholder="Ex: Lanchonete do Zé" />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-bold text-maraca-navy">Identificador de acesso</label>
              <div className="mb-2 flex gap-2">
                {["cnpj", "cpf", "email"].map((t) => (
                  <button key={t} type="button" onClick={() => setIdType(t)} data-testid={`idtype-${t}`}
                    className={`rounded-full px-4 py-1.5 text-sm font-bold uppercase transition-colors ${idType === t ? "bg-maraca-navy text-white" : "bg-slate-100 text-maraca-navy/70"}`}>
                    {t}
                  </button>
                ))}
              </div>
              <input data-testid="input-identifier" value={form.identifier} onChange={set("identifier")} className="w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-maraca-teal" placeholder={idPlaceholder[idType]} />
            </div>

            {idType !== "email" && (
              <div>
                <label className="mb-1.5 block text-sm font-bold text-maraca-navy">E-mail</label>
                <input data-testid="input-email" type="email" value={form.email} onChange={set("email")} className="w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-maraca-teal" placeholder="seu@email.com" />
              </div>
            )}

            <div>
              <label className="mb-1.5 block text-sm font-bold text-maraca-navy">WhatsApp</label>
              <input data-testid="input-phone" value={form.phone} onChange={set("phone")} className="w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-maraca-teal" placeholder="(00) 0 0000-0000" />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-bold text-maraca-navy">Senha</label>
              <input data-testid="input-password" type="password" value={form.password} onChange={set("password")} className="w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-maraca-teal" placeholder="Crie uma senha" />
            </div>

            <button disabled={loading} data-testid="cadastro-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-maraca-navy px-6 py-3.5 font-display text-base font-bold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
              {loading ? "Criando conta…" : "Continuar"}
              {!loading && <ArrowRight size={18} weight="bold" />}
            </button>
          </form>
        )}

        {step === 2 && (
          <div className="mt-8" data-testid="cadastro-images">
            <h2 className="font-display text-2xl font-bold text-maraca-navy">Identidade visual</h2>
            <p className="mt-1 text-sm text-maraca-navy/60">Envie a logomarca (aparece nos cards) e uma foto de capa (topo da sua loja).</p>
            <div className="mt-6 flex flex-col gap-6">
              <div className="flex items-center gap-4">
                <ImagePicker kind="logo" label="Adicionar logo" hint="1:1" value={logoUrl} onChange={setLogoUrl} testid="picker-logo" />
                <span className="text-sm text-maraca-navy/60">Logomarca da loja (quadrada)</span>
              </div>
              <ImagePicker kind="cover" label="Adicionar capa" hint="16:9" value={coverUrl} onChange={setCoverUrl} testid="picker-cover" />
            </div>
            <button disabled={loading} onClick={finish} data-testid="cadastro-finish" className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-maraca-teal px-6 py-3.5 font-display text-base font-bold text-maraca-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
              {loading ? "Salvando…" : "Finalizar cadastro"}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="mt-10 rounded-3xl border border-black/5 bg-slate-50 p-10 text-center" data-testid="cadastro-success">
            <CheckCircle size={56} weight="fill" className="mx-auto text-maraca-teal" />
            <h2 className="mt-4 font-display text-2xl font-bold text-maraca-navy">Loja cadastrada com sucesso!</h2>
            <p className="mt-2 text-maraca-navy/70">Agora escolha um plano para ativar sua loja no app.</p>
            <button onClick={() => navigate("/planos")} data-testid="go-planos" className="mt-6 inline-flex items-center gap-2 rounded-full bg-maraca-navy px-7 py-3.5 font-display font-bold text-white transition-transform duration-300 hover:-translate-y-0.5">
              Escolher plano <ArrowRight size={18} weight="bold" />
            </button>
            <div className="mt-4">
              <Link to="/" className="text-sm font-semibold text-maraca-navy/60 hover:text-maraca-navy">Voltar ao site</Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
